import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ASSET_PATHS from "../../../assets/config/assetPaths";
import LogoutModal from "../Modal/LogoutModal";
import { StatusBar } from "../StatusBar/StatusBar";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store.ts";
import { version } from "../../../../package.json";
import {
  isOfflineMode as getOfflineMode,
  turnOfflineModeOn,
  turnOfflineModeOff,
  onOfflineModeChange,
} from "../../../shared/offlineManager";

interface TopBarProps {
  title: string;
  showBackIcon?: boolean;
  isDarkMode?: boolean;
  navigateTo?: string;
}

const TopBar: React.FC<TopBarProps> = ({
  title,
  showBackIcon = false,
  isDarkMode = false,
  navigateTo,
}) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isOfflineMode, setIsOfflineMode] = useState(() => getOfflineMode());
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const unsubscribe = onOfflineModeChange(setIsOfflineMode);
    return unsubscribe;
  }, []);

  const handleBackClick = () => {
    if (showBackIcon) {
      navigateTo ? navigate(navigateTo) : navigate(-1);
    }
  };

  const handleMenuClick = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  const navigateToPendingRequests = () => {
    navigate(`/pending-requests`);
  };

  const tenant = useSelector((state: RootState) => state.auth.tenant);
  const { failedCount } = useSelector(
    (state: RootState) => state.pendingUploadsQueue
  );

  return (
    <>
      <StatusBar />
      <div
        className={`relative p-4 pt-8 flex justify-between items-center ${
          isDarkMode
            ? "bg-primary text-white pb-6"
            : "bg-gray-100 text-black pb-3"
        }`}
      >
        <button
          onClick={handleBackClick}
          className={`${showBackIcon ? "visible" : "invisible"}`}
        >
          <img src={ASSET_PATHS.backIcon} alt="Back Icon" className="w-6 h-6" />
        </button>

        <h1 className="text-xl font-semibold">{title}</h1>

        {failedCount > 0 && (
          <div
            className="absolute top-8 right-12 bg-red-500 text-white rounded-full flex items-center justify-center cursor-pointer w-6 h-6 text-xs font-bold z-50"
            onClick={navigateToPendingRequests}
          >
            {failedCount}
          </div>
        )}

        <button onClick={handleMenuClick} ref={buttonRef}>
          <img
            src={isDarkMode ? ASSET_PATHS.menuIcon : ASSET_PATHS.menuBlackIcon}
            alt="Menu Icon"
            className="w-6 h-6"
          />
        </button>

        {isMenuOpen && (
          <div
            ref={menuRef}
            className="absolute top-16 right-4 bg-white shadow-lg rounded-md w-40 z-10 text-md"
          >
            <div className="flex flex-col space-y-2">
              <button
                className="flex flex-col space-x-2 text-textPrimary p-3 pb-0"
                disabled
              >
                <span className="ml-2">{version}</span>
                <span className="ml-2">Company {tenant ?? "---"}</span>
              </button>

              <div className="border-t border-gray-300"></div>
              <div className="flex m-auto transition text-gray-700">{`You are ${
                isOfflineMode ? "Offline" : "Online"
              }`}</div>

              <div className="flex justify-between px-3 py-1 w-full">
                {isOfflineMode ? (
                  <button
                    onClick={turnOfflineModeOff}
                    className={`text-xs px-3 py-2 rounded-md border  w-full ${
                      isOfflineMode
                        ? "bg-primary text-white border-primary"
                        : "bg-gray-100 text-gray-700 border-gray-300"
                    }`}
                  >
                    Go Online
                  </button>
                ) : (
                  <button
                    onClick={turnOfflineModeOn}
                    className={`text-xs px-3 py-2 rounded-md border transition w-full ${
                      !isOfflineMode
                        ? "bg-primary text-white border-primary"
                        : "bg-gray-100 text-gray-700 border-gray-300"
                    }`}
                  >
                    Go Offline
                  </button>
                )}
              </div>

              <div className="border-t border-gray-300"></div>

              <button
                onClick={navigateToPendingRequests}
                className="flex items-center space-x-2 p-3 pt-0 text-red-500"
              >
                <img
                  src={ASSET_PATHS.pendingAction}
                  alt="Pending Icon"
                  className="w-5 h-5"
                />
                <span>Pending</span>
              </button>

              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 p-3 pt-0 text-red-500"
              >
                <img
                  src={ASSET_PATHS.logout}
                  alt="Logout Icon"
                  className="w-5 h-5"
                />
                <span>Logout</span>
              </button>
            </div>
          </div>
        )}

        {showLogoutModal && (
          <LogoutModal
            onClose={() => setShowLogoutModal(false)}
            isOpen={showLogoutModal}
          />
        )}
      </div>
    </>
  );
};

export default TopBar;
